import React, { useRef, useState, useEffect } from 'react';
import styles from './ManageExistingSolutions.styles.scss';
import { MainLayout } from '../../components/MainLayout';
import { PagesHeader } from '../../components/shared/PagesHeader';
import { useTranslation } from 'react-i18next';
import {
  COLUMNS,
  ROUTES,
  SEARCH_CONFIG,
} from './ManageExistingSolutions.config';
import { useAuthentication } from '../../hooks/useAuthentication';
import { Button } from 'primereact/button';
import DataTable from '../../components/shared/DataTable/DataTable';
import { useNavigate } from 'react-router';
import DeregistrationDialog from './components/DeregistrationDialog/DeregistrationDialog';
import { PERMISSIONS } from '../../helpers/auth';
import cn from 'classnames';
import moment from 'moment';

const EXPORT_DATE_FIELDS = [
  'onBoardingDate',
  'certifiedExpiryDate',
  'revocationDate',
];

const formatExportDate = (value) => {
  if (!value || value === '-') {
    return value;
  }

  const parsedDate = moment(
    value,
    ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601],
    true,
  );

  return parsedDate.isValid()
    ? parsedDate.format('DD/MM/YYYY  HH:mm:ss')
    : value;
};

const ManageExistingSolutions = ({
  solutions,
  loading,
  tableLoading,
  setRenderSolutions,
  originalSolutions,
  selectedSolutions,
  setSelectedSolutions,
  onRevokeClick,
}) => {
  const { t } = useTranslation();
  const { hasRole } = useAuthentication();
  const history = useNavigate();

  const dtRef = useRef(null);

  const [isDeregisterOpen, setIsDeregisterOpen] = useState(false);
  const [visibleRowCount, setVisibleRowCount] = useState(0);

  useEffect(() => {
    setVisibleRowCount(solutions?.length ?? 0);
  }, [solutions]);

  const isDataSelectable = (row) => row.data.status === 'ACTIVE';

  const cellClassName = (data, options) =>
    options.field === 'field_0' &&
    !isDataSelectable({ data: solutions[options.rowIndex] })
      ? 'p-disabled'
      : '';

  return (
    <MainLayout eInvoicing={true} bannerClassName={styles.banner}>
      <PagesHeader
        withBreadcrumb={true}
        title={t('view_list_of_devices')}
        routes={ROUTES}
        titleClassName={styles.title}
        withActionButton={true}
        action={
          hasRole(PERMISSIONS.ADD_DEVICE) ? (
            <Button
              className="px-4"
              disabled={!hasRole(PERMISSIONS.ADD_DEVICE)}
              label={t('add_amend_device')}
              onClick={() =>
                hasRole(PERMISSIONS.ADD_DEVICE) &&
                history('./initiate-new-registration')
              }
            />
          ) : null
        }
      />
      <div className={cn(styles.solutionsList, 'mt-3')}>
        <DataTable
          ref={dtRef}
          isDataSelectable={isDataSelectable}
          cellClassName={cellClassName}
          loading={tableLoading}
          value={solutions}
          selectedRows={selectedSolutions}
          setSelectedRows={setSelectedSolutions}
          filterDisplay="menu"
          columns={COLUMNS}
          searchConfig={SEARCH_CONFIG}
          showSortInFilters={true}
          noDataDesc={t('solution_list.no_solutions_desc')}
          exportFunction={({ data, field, rowData, column }) => {
            // Status values need to be translated
            if (field === 'status') {
              return data ? t(`solutionList.${data}`) : data;
            }
            if (EXPORT_DATE_FIELDS.includes(field)) {
              return formatExportDate(data);
            }
            // Use the column body renderer when it returns a plain string
            // (covers invoiceType, date columns, solutionSerialNumber, etc.)
            const bodyResult = column?.props?.body?.(rowData);
            if (typeof bodyResult === 'string') {
              return bodyResult;
            }
            return data;
          }}
          dataKey="requestId"
          exportFilename="devices_list"
          searchFields={['organizationName']}
          selectable={hasRole(PERMISSIONS.REVOKE)}
          onValueChange={(filteredData) =>
            setVisibleRowCount(filteredData?.length ?? 0)
          }
        />
      </div>
      <div className="d-flex justify-content-end align-items-center mt-3 gap-3">
        <Button
          outlined
          className="col-2"
          variant="outlined"
          id="manage_reset_btn"
          disabled={visibleRowCount === 0}
          label={t('export-list')}
          onClick={() => dtRef?.current.exportCSV()}
        />
        {hasRole(PERMISSIONS.REVOKE) ? (
          <Button
            type="submit"
            className="col-2"
            id="manage_filter_btn"
            disabled={!selectedSolutions.length || !hasRole(PERMISSIONS.REVOKE)}
            label={t('deregister')}
            loading={loading}
            onClick={() => {
              setIsDeregisterOpen(true);
            }}
          />
        ) : null}
        <DeregistrationDialog
          isOpen={isDeregisterOpen}
          setIsOpen={setIsDeregisterOpen}
          handleSubmit={({ reason, description }) => {
            return onRevokeClick(
              selectedSolutions.map((s) => s.requestId),
              reason,
              description,
            );
          }}
        />
      </div>
    </MainLayout>
  );
};

export default React.memo(ManageExistingSolutions);
